import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, mapTo, pluck, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { getFracName } from 'src/app/ui-components/pipes/frac-name.pipe';
import { SettingsService } from '~billing/services/settings.service';
import { LmoFracListSummary } from '~lmo/models/frac.model';
import { FracClusterPurchaseOrder, PurchaseOrder, PurchaseOrderUpdate } from '~lmo/models/purchaseOrders.model';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { LmoFracMineService } from '~lmo/services/lmo-frac-mine.service';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { User } from '~models/user';
import { UserApiService } from '~services/api/user.api.service';
import { AuthService } from '~services/auth.service';
import { UserService } from '~services/user.service';
import { trackById } from '~utilities/trackById';
import { ConfirmActionDialogComponent } from '../../../ui-components/confirm-action-dialog/confirm-action-dialog.component';

const nullPoForm: Partial<EditPoFormValue> = {
  applyToCompletedOrder: false,
  costPerTon: null,
  dailyMaxTon: null,
  dailyMinTon: null,
  id: null,
  isBlanket: false,
  permittedFracIds: [],
  resetAllocationTime: null,
  subscribedSandiUsers: [],
  totalTonnage: null,
};

interface EditPoFormValue {
  costPerTon: number;
  dailyMaxTon: number;
  dailyMinTon: number;
  id: number;
  permittedFracIds: number[];
  resetAllocationTime: Date;
  subscribedSandiUsers: number[];
  totalTonnage: number;
  isBlanket: boolean;
  blanketAllocations: {
    fracId: number;
    allocationTonnage: number;
  }[];
  billableMileage?: number;
  defaultLoadWeight?: number;
  poLineItemNumber?: number;
  percentBasedAutoOrderingAllocation?: number;
  applyToCompletedOrder: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-fcpo-manage-id',
  styleUrls: ['./lmo-frac-fcpo-manage-id.component.scss'],
  templateUrl: './lmo-frac-fcpo-manage-id.component.html',
})
export class LmoFracFcpoManageIdComponent implements OnInit, OnDestroy {
  public isGroupedAccountUser$: Observable<boolean>;
  @ViewChild('confirmCancel', { static: true }) private confirmCancel: TemplateRef<any>;
  @ViewChild('pauseDescription', { static: true }) private pauseModal: TemplateRef<any>;
  @ViewChild('defaultLoadWeightWarning', { static: true }) private defaultLoadWeightWarningModal: TemplateRef<any>;
  @ViewChild('billableMilageApplySameMineApplyToCompletedOrders', { static: true })
  private billableMilageApplySameMineApplyToCompletedOrders: TemplateRef<any>;
  @ViewChild('billableMilageApplySameMineNotApplyToCompletedOrders', { static: true })
  private billableMilageApplySameMineNotApplyToCompletedOrders: TemplateRef<any>;
  @ViewChild('billableMilageApplyToCompletedOrders', { static: true })
  private billableMilageApplyToCompletedOrders: TemplateRef<any>;
  @ViewChild('billableMilageNotApplyToCompletedOrders', { static: true })
  private billableMilageNotApplyToCompletedOrders: TemplateRef<any>;
  public trackById = trackById;
  public currentFracName$: Observable<string>;
  public autoPurchaseOrderAssignment$: Observable<boolean>;
  public existingFcpo$: Observable<FracClusterPurchaseOrder>;
  public editPoForm: FormGroup;
  public billableMileage: FormControl = new FormControl(null, [Validators.required, Validators.min(0)]);
  public percentBasedAutoOrderingAllocation: FormControl = new FormControl(null);
  public defaultLoadWeight: FormControl = new FormControl(null, [Validators.min(10000), Validators.max(42000)]);
  public poLineItemNumber: FormControl = new FormControl(null, []);
  public selectedPo: FormControl = new FormControl(null, [Validators.required]);
  public floor = Math.floor;
  public currentFcpo$: Observable<FracClusterPurchaseOrder>;
  public meshPos$: Observable<PurchaseOrder[]>;
  public fracList$: Observable<LmoFracListSummary[]>;
  public uploadingPOAttachment$$ = new BehaviorSubject(false);
  public pauseReason = new FormControl(null, [Validators.required]);
  public fracListForBlanketPO$: Observable<LmoFracListSummary[]>;
  private lmoId: number;
  private poLineNumberValue: any;
  public isPOLineItemNumberMandatory = false;
  private showPromptOnChangeBillableMileage$$ = new BehaviorSubject<boolean>(false);
  private billableMileageInitialValue$$ = new BehaviorSubject<number>(null);

  public filterUser: FormControl = new FormControl();
  public filteredUsers$: Observable<User[]>;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private users$$ = new BehaviorSubject<User[]>([]);

  public currentMineName$ = new BehaviorSubject<string>(null);
  public showApplyToCompletedOrdersToggle$ = new BehaviorSubject<boolean>(false);

  public updatingPO$ = new BehaviorSubject<boolean>(false);

  constructor(
    private fracService: LmoFracsService,
    private fb: FormBuilder,
    private fcpoService: LmoFracFcpoService,
    private modalService: LmoFracModalService,
    private fracMineService: LmoFracMineService,
    private matDialog: MatDialog,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private usersService: UserService,
    private settingService: SettingsService,
    private userApiService: UserApiService,
    private cd: ChangeDetectorRef,
  ) {
    this.userApiService.getWebUsers().subscribe((users) => {
      this.users$$.next(users);
    });

    this.filteredUsers$ = combineLatest([this.users$$, this.filterUser.valueChanges.pipe(startWith(''))]).pipe(
      map(([users, searchFilter]) => {
        if (!searchFilter) {
          return users;
        }

        const searchFilterValue = searchFilter.toLowerCase();
        return users.filter((option) => option.name.toLowerCase().indexOf(searchFilterValue) === 0);
      }),
      takeUntil(this.destroy$),
    );
  }

  public isVorto() {
    return this.usersService.isVortoUser() || this.usersService.isShaleappsEmail();
  }

  public ngOnInit() {
    this.currentFcpo$ = this.fcpoService.currentFcpo$.pipe(
      filter((val) => !!val),
      tap((frac) => {
        this.showPromptOnChangeBillableMileage$$.next(!!frac.showPromptOnChangeBillableMileage);
      }),
    );
    this.currentFracName$ = this.fracService.currentFrac$.pipe(map(getFracName));
    this.fracList$ = this.fracService.fracSummaries$;
    this.autoPurchaseOrderAssignment$ = this.fracService.currentFrac$.pipe(
      filter(Boolean),
      pluck('autoPurchaseOrderAssignment'),
    );
    const blanketAllocations = this.fb.array([]);
    this.editPoForm = this.fb.group({
      ...nullPoForm,
      blanketAllocations,
    });
    this.meshPos$ = combineLatest([this.fcpoService.purchaseOrders$, this.currentFcpo$]).pipe(
      map(([purchaseOrders, fcpo]: [PurchaseOrder[], FracClusterPurchaseOrder]) => {
        if (fcpo && fcpo.purchaseOrder.meshId && fcpo.purchaseOrder.mineId) {
          return purchaseOrders.filter(
            (po) => po.meshId === fcpo.purchaseOrder.meshId && po.mineId === fcpo.purchaseOrder.mineId,
          );
        }
        return purchaseOrders;
      }),
      tap((POs) => {
        POs.sort((a, b) => (a.allocation - (a.ordered || 0) < b.allocation - (b.ordered || 0) ? 1 : -1));
      }),
    );
    this.setupForm();
    this.lmoId = this.usersService.accountId();
    this.settingService.loadSettings(this.lmoId).subscribe((settings) => {
      this.isPOLineItemNumberMandatory = settings.isPOLineItemNumberMandatory;
      if (!this.poLineNumberValue && settings.isPOLineItemNumberMandatory) {
        this.poLineItemNumber.setValue(settings.defaultPOLineItemNumber);
      }
    });
    this.listenForPoChanges();
    this.listenForBillableMileageChanges();
    this.isGroupedAccountUser$ = this.authService.isGroupedAccountUser$;
    this.setupFracListForBlanket();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    // Just in case
    this.destroy$.unsubscribe();
  }

  private setupForm() {
    this.existingFcpo$ = this.currentFcpo$.pipe(
      filter((fcpo) => !!fcpo),
      tap(async (fcpo) => {
        this.fracMineService.getFracMineRoutes$(fcpo.purchaseOrder.mineId).subscribe((results) => {
          this.billableMileageInitialValue$$.next(results.billableMileage);
          this.billableMileage.setValue(results.billableMileage);
          this.defaultLoadWeight.setValue(results.defaultLoadWeightPounds);
          this.percentBasedAutoOrderingAllocation.setValue(fcpo.percentBasedAutoOrderingAllocation);
        });
        this.poLineNumberValue = fcpo.poLineItemNumber;
        const pos = await this.fcpoService.purchaseOrders$
          .pipe(
            filter((po) => !!po && !!po.length),
            take(1),
          )
          .toPromise();
        const actualPO = pos.find((po) => po.id === fcpo.purchaseOrder.id);
        this.selectedPo.setValue(actualPO);
        this.selectedPo.setValue({ ...actualPO, subscribedUsers: fcpo.purchaseOrder.subscribedUsers });
        this.poLineItemNumber.patchValue(fcpo.poLineItemNumber);
        this.currentMineName$.next(fcpo.purchaseOrder.mineName);
      }),
    );
  }

  private listenForPoChanges() {
    this.selectedPo.valueChanges.subscribe((po: PurchaseOrder) => {
      this.editPoForm.patchValue(convertPurchaseOrderToEditPoForm(po));
      const blanketAllocations = this.editPoForm.get('blanketAllocations') as FormArray;
      while (blanketAllocations.length) {
        blanketAllocations.removeAt(0);
      }
      (po.blanketAllocations || []).forEach((ba) => {
        const fg = this.fb.group({
          allocationTonnage: ba.allocationTonnage,
          fracId: ba.fracId,
        });
        blanketAllocations.push(fg);
      });
    });
  }

  private listenForBillableMileageChanges() {
    this.billableMileage.valueChanges.subscribe((value) => {
      this.showApplyToCompletedOrdersToggle$.next(this.billableMileageInitialValue$$.value !== value);
    });
  }

  private setupFracListForBlanket() {
    this.fracListForBlanketPO$ = combineLatest([
      this.fracList$,
      this.editPoForm
        .get('blanketAllocations')
        .valueChanges.pipe(startWith(this.editPoForm.get('blanketAllocations').value)),
    ]).pipe(
      map(([fracList, blankets]: [LmoFracListSummary[], { fracId: number }[]]) => {
        const blanketIDs = blankets.map((b) => b.fracId);
        return (fracList || []).filter((f) => {
          return !blanketIDs.includes(f.lmoFracSummary.id);
        });
      }),
    );
  }

  public async updatePo() {
    if (
      this.editPoForm.invalid ||
      this.selectedPo.invalid ||
      this.billableMileage.invalid ||
      this.defaultLoadWeight.invalid ||
      (this.poLineItemNumber.invalid && this.isPOLineItemNumberMandatory) ||
      (this.poLineItemNumber.value && !this.poLineItemNumber.value.trim() && this.isPOLineItemNumberMandatory)
    ) {
      return;
    }

    const defaultLoadWeightValue = this.defaultLoadWeight.value;
    // Less than half of the default load weight
    if (defaultLoadWeightValue !== null && defaultLoadWeightValue < 21000) {
      const confirmed = await this.matDialog
        .open(this.defaultLoadWeightWarningModal, {
          panelClass: ['w-full', 'lg:w-1/2'],
        })
        .afterClosed()
        .toPromise();

      if (!confirmed) {
        return;
      } else {
        this.cd.markForCheck();
      }
    }

    const applyToCompletedOrders = this.editPoForm.get('applyToCompletedOrder').value;
    if (this.showPromptOnChangeBillableMileage$$.value) {
      if (this.billableMileageInitialValue$$.value !== this.billableMileage.value && !applyToCompletedOrders) {
        const confirmed = await this.matDialog
          .open(this.billableMilageApplySameMineNotApplyToCompletedOrders, {
            panelClass: ['w-full', 'lg:w-1/2'],
          })
          .afterClosed()
          .toPromise();

        if (!confirmed) {
          return;
        } else {
          this.cd.markForCheck();
        }
      } else if (this.billableMileageInitialValue$$.value !== this.billableMileage.value && applyToCompletedOrders) {
        const confirmed = await this.matDialog
          .open(this.billableMilageApplySameMineApplyToCompletedOrders, {
            panelClass: ['w-full', 'lg:w-1/2'],
          })
          .afterClosed()
          .toPromise();

        if (!confirmed) {
          return;
        } else {
          this.cd.markForCheck();
        }
      }
    } else {
      if (this.billableMileageInitialValue$$.value !== this.billableMileage.value && !applyToCompletedOrders) {
        const confirmed = await this.matDialog
          .open(this.billableMilageNotApplyToCompletedOrders, {
            panelClass: ['w-full', 'lg:w-1/2'],
          })
          .afterClosed()
          .toPromise();

        if (!confirmed) {
          return;
        } else {
          this.cd.markForCheck();
        }
      } else if (this.billableMileageInitialValue$$.value !== this.billableMileage.value && applyToCompletedOrders) {
        const confirmed = await this.matDialog
          .open(this.billableMilageApplyToCompletedOrders, {
            panelClass: ['w-full', 'lg:w-1/2'],
          })
          .afterClosed()
          .toPromise();

        if (!confirmed) {
          return;
        } else {
          this.cd.markForCheck();
        }
      }
    }
    const value = this.editPoForm.value as EditPoFormValue;

    value.billableMileage = this.billableMileage.value;
    value.defaultLoadWeight = this.defaultLoadWeight.value;
    value.percentBasedAutoOrderingAllocation = this.percentBasedAutoOrderingAllocation.value || null;
    value.poLineItemNumber =
      this.poLineItemNumber.value && this.poLineItemNumber.value.trim() ? this.poLineItemNumber.value.trim() : null;

    this.updatingPO$.next(true);

    this.currentFcpo$
      .pipe(
        take(1),
        switchMap((currentFcpo) => {
          const billableMileage = this.billableMileage.value;
          let defaultLoadWeight = +this.defaultLoadWeight.value;
          if (!defaultLoadWeight) {
            defaultLoadWeight = null;
          }
          const mineId = currentFcpo.purchaseOrder.mineId;
          const purchaseOrderID = currentFcpo.purchaseOrder.id;

          return this.fracMineService
            .setFracMineRoute$(
              mineId,
              billableMileage,
              defaultLoadWeight,
              applyToCompletedOrders,
              purchaseOrderID,
              currentFcpo.fracClusterId,
            )
            .pipe(
              catchError(() => of(null)), // Ignore any errors from this and just keep on updating the PO
              mapTo(currentFcpo), // Back to FCPO, this just needs to happen before an update for scheduler stuff.
            );
        }),
        switchMap((currentFcpo) =>
          this.fcpoService.updateFcpo$(currentFcpo.id, {
            blanketAllocations: value.isBlanket ? value.blanketAllocations : null,
            fracClusterId: currentFcpo.fracClusterId,
            id: currentFcpo.id,
            percentBasedAutoOrderingAllocation: this.percentBasedAutoOrderingAllocation.value || null,
            poLineItemNumber:
              this.poLineItemNumber.value && this.poLineItemNumber.value.trim()
                ? this.poLineItemNumber.value.trim()
                : null,
            purchaseOrder: convertPoEditFormValueToPurchaseOrderUpdate(value),
          }),
        ),
      )
      .subscribe((successful) => {
        if (successful) {
          this.modalService.closeHomeModal();
          this.fcpoService.loadAccountPurchaseOrders();
          this.fracService.reloadCurrentFrac$().subscribe();
        }
        this.updatingPO$.next(false);
      });
  }

  public removePo() {
    const dialogRef = this.matDialog.open(this.confirmCancel, {
      disableClose: true,
      maxWidth: '968px',
      minWidth: '300px',
      width: '30%',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.currentFcpo$
          .pipe(
            take(1),
            switchMap((fcpo) => {
              return this.fcpoService.removeFcpo$(fcpo.id);
            }),
          )
          .subscribe((successful: boolean) => {
            if (successful) {
              this.modalService.closeHomeModal();
            }
          });
      }
    });
  }

  public onFileChange(event, existingFcpo: FracClusterPurchaseOrder) {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      if (this.isImage(file.name) || this.isPDF(file.name)) {
        this.uploadAttachments(file, existingFcpo);
      } else {
        this.snackBar.open('Image and PDF files can only be uploaded.', null, {
          duration: 5000,
          panelClass: ['snackbar-error'],
        });
      }
    }
  }

  private uploadAttachments(file, existingFcpo: FracClusterPurchaseOrder) {
    this.uploadingPOAttachment$$.next(true);
    const fileName =
      existingFcpo.purchaseOrder.mineName +
      '-' +
      existingFcpo.purchaseOrder.meshType +
      '-' +
      existingFcpo.purchaseOrder.id;
    this.fcpoService.uploadPOAttachment$(existingFcpo.purchaseOrder.id, file, fileName).subscribe(
      (res) => {
        this.uploadingPOAttachment$$.next(false);
        this.fcpoService.loadFCPOs(existingFcpo.fracClusterId);
        this.snackBar.open('File uploaded Successfully', null, {
          duration: 5000,
        });
      },
      (err) => {
        this.uploadingPOAttachment$$.next(false);
        this.snackBar.open('Upload Failed!', null, {
          duration: 5000,
        });
        console.error(err, 'Upload Failed', file);
      },
    );
  }

  public calculatePoRemaining(po: PurchaseOrder): number {
    const remaining = po.allocation - (po.pickedUp || 0);
    if (remaining < 0) {
      return 0;
    }
    return remaining;
  }

  public compareById<T extends { id: number }>(a: T, b: T): boolean {
    if (!a || !b) {
      return false;
    }
    return a.id === b.id;
  }

  public deleteAttachment(fileId, poId, fracClusterId) {
    const confirmDialogRef = this.matDialog.open(ConfirmActionDialogComponent, {
      data: {
        cancelButtonText: 'Cancel',
        desc: 'Are you sure you want delete this attachment? This cannot be undone.',
        submitButtonText: 'Delete',
        title: `Delete Attachment`,
      },
    });

    confirmDialogRef.afterClosed().subscribe((keepGoing) => {
      if (!keepGoing) {
        return;
      }

      this.fcpoService.deletePOAttachment$(fileId, poId).subscribe(
        (res) => {
          this.fcpoService.loadFCPOs(fracClusterId);
          this.snackBar.open('Attachment Deleted Successfully', null, {
            duration: 5000,
          });
        },
        (err) => {
          this.uploadingPOAttachment$$.next(false);
          this.snackBar.open('Deletion Failed!', null, {
            duration: 5000,
          });
          console.error(err, 'Deletion Failed', fileId, poId);
        },
      );
    });
  }

  public getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return fileName.substring(lastDot + 1);
  }

  public isImage(fileName): boolean {
    const imageExtensions = ['jpeg', 'jpg', 'png'];
    return imageExtensions.includes(this.getFileExtension(fileName));
  }

  public isPDF(fileName): boolean {
    return this.getFileExtension(fileName) === 'pdf';
  }

  public openInWindow(fileUrl) {
    window.open(fileUrl, '_blank');
  }

  public promptPause() {
    this.matDialog.open(this.pauseModal, {
      panelClass: ['w-full', 'lg:w-1/2'],
    });
  }

  public async pausePO() {
    if (this.pauseReason.invalid) {
      return;
    }
    const currentPO = await this.currentFcpo$.pipe(take(1)).toPromise();
    try {
      await this.fcpoService.pausePO(currentPO.purchaseOrder.id, this.pauseReason.value);
      this.fcpoService.loadFCPOs(currentPO.fracClusterId);
      this.snackBar.open('PO Paused', null, { duration: 5000 });
    } catch (e) {
      this.snackBar.open('Failed to pause PO', null, { duration: 5000 });
    }
  }

  public async unpausePO() {
    const currentPO = await this.currentFcpo$.pipe(take(1)).toPromise();
    try {
      await this.fcpoService.unpausePO(currentPO.purchaseOrder.id);
      this.fcpoService.loadFCPOs(currentPO.fracClusterId);
      this.snackBar.open('PO Resumed', null, { duration: 5000 });
    } catch (e) {
      this.snackBar.open('Failed to resume PO', null, { duration: 5000 });
    }
  }

  public addBlanketAllocation() {
    const fa = this.editPoForm.get('blanketAllocations') as FormArray;
    fa.push(
      this.fb.group({
        allocationPercent: [null, []],
        allocationTonnage: [0, [Validators.required]],
        fracId: [null, [Validators.required]],
      }),
    );
  }

  public removeBlanketAllocation(index: number) {
    const fa = this.editPoForm.get('blanketAllocations') as FormArray;
    let current = fa.at(index).get('allocationTonnage').value;
    fa.removeAt(index);
    if (fa.length === 0) {
      return;
    }
    const updatedAllocs: number[] = fa.controls.map((c) => c.get('allocationTonnage').value as number);
    const totalPercent = () => updatedAllocs.reduce((s, e) => s + e, 0);
    while (current > 0) {
      for (let i = 0; i < updatedAllocs.length; i++) {
        if (totalPercent() >= 100) {
          current = 0;
          break;
        }
        if (current > 0) {
          const toAdd = current >= 1 ? 1 : current;
          updatedAllocs[i] += toAdd;
          current -= toAdd;
        }
      }
    }
    for (let i = 0; i < updatedAllocs.length; i++) {
      fa.at(i)
        .get('allocationTonnage')
        .setValue(updatedAllocs[i]);
    }
  }
}

function convertPurchaseOrderToEditPoForm(po: PurchaseOrder): Partial<EditPoFormValue> {
  const offsetAsTime = moment()
    .startOf('day')
    .add(po.resetAllocationTimeOffset, 'minutes');

  return {
    blanketAllocations: [],
    costPerTon: po.costPerTon,
    dailyMaxTon: po.maximumDailyAllocation ? po.maximumDailyAllocation / 2000 : 0,
    dailyMinTon: po.minimumDailyAllocation ? po.minimumDailyAllocation / 2000 : 0,
    id: po.id,
    isBlanket: po.blanketAllocations && !!po.blanketAllocations.length,
    permittedFracIds: po.permittedFracIds || [],
    resetAllocationTime: offsetAsTime.toDate(),
    subscribedSandiUsers: po.subscribedUsers ? po.subscribedUsers?.map((user) => user.id) : [],
    totalTonnage: po.allocation / 2000,
  };
}

function convertPoEditFormValueToPurchaseOrderUpdate(formValue: EditPoFormValue): PurchaseOrderUpdate {
  const pickedValue = moment(formValue.resetAllocationTime);
  const startOfDay = pickedValue.clone().startOf('day');
  const minutesOffSet = pickedValue.diff(startOfDay, 'minutes');
  return {
    allocation: formValue.totalTonnage * 2000,
    billableMileage: formValue.billableMileage,
    blanketAllocations: formValue.isBlanket ? formValue.blanketAllocations : null,
    costPerTon: formValue.costPerTon,
    defaultLoadWeight: formValue.defaultLoadWeight,
    id: formValue.id,
    maximumDailyAllocation: formValue.dailyMaxTon * 2000,
    minimumDailyAllocation: formValue.dailyMinTon * 2000,
    percentBasedAutoOrderingAllocation: formValue.percentBasedAutoOrderingAllocation,
    permittedFracIds: formValue.permittedFracIds,
    poLineItemNumber: formValue.poLineItemNumber,
    resetAllocationTimeOffset: minutesOffSet,
    subscribedUserIds: formValue.subscribedSandiUsers,
  };
}
